/**
 * NAO-97: the photo viewer. A tap on a gallery photo opens every photo on show, whole
 * (object-fit contain), one per screen, side by side on a native scroll-snap track, at the
 * photo tapped. It replaces Dawn's media modal (product-modal.js), which opened a zoomed
 * crop. Markup: snippets/product-media-gallery.liquid. Styles: assets/pdp-gallery.css.
 *
 * A native <dialog> opened with showModal sits in the top layer, so the peek drawer and
 * the bar stay under it with no z-index, the page behind is inert (focus stays inside),
 * and Esc closes it. body.overflow-hidden locks the page and steps the bar and the drawer
 * out (pdp-gallery.css), as every other overlay does; closing puts both back untouched.
 * A strip of small thumbs under the track jumps straight to any photo.
 */
(() => {
  let opener = null;
  let scrollY = 0;

  // the colour's photos, as pdp-bar.js photos() (a colour change hides the other colours'
  // frames), plus the ones the phone cap keeps out of the stack (media-gallery.js): the viewer
  // always holds the whole colour. ponytail: images only; a video or 3D frame keeps Dawn's modal.
  const photos = () =>
    Array.from(document.querySelectorAll('.pdp-gallery__stack > .product__media-item')).filter(
      (item) =>
        (item.offsetParent !== null || item.classList.contains('is-capped')) &&
        item.querySelector('.product__modal-opener--image img')
    );

  const parts = (box) => ({
    track: box.querySelector('[data-pdp-lightbox-track]'),
    thumbs: box.querySelector('[data-pdp-lightbox-thumbs]'),
    count: box.querySelector('[data-pdp-lightbox-count]'),
    prev: box.querySelector('[data-pdp-lightbox-step="-1"]'),
    next: box.querySelector('[data-pdp-lightbox-step="1"]'),
  });

  const current = (track) => {
    const slide = track.firstElementChild;
    // slides sit 1px apart (the hairline fix in pdp-gallery.css)
    return slide ? Math.round(track.scrollLeft / (slide.offsetWidth + 1)) : 0;
  };

  // runs once per frame at most while the track scrolls; the DOM is only written when the
  // photo changes
  let shown = -1;
  const sync = (box) => {
    const { track, thumbs, count, prev, next } = parts(box);
    const total = track.childElementCount;
    const index = Math.min(current(track), total - 1);
    if (index === shown) return;
    shown = index;
    count.textContent = `${index + 1} / ${total}`;
    prev.disabled = index === 0;
    next.disabled = index === total - 1;
    thumbs.querySelector('[aria-current]')?.removeAttribute('aria-current');
    const thumb = thumbs.children[index]?.firstElementChild;
    if (!thumb) return;
    thumb.setAttribute('aria-current', 'true');
    // keep the active thumb in view, centred when the strip overflows (an instant jump)
    const left = thumb.offsetLeft - (thumbs.clientWidth - thumb.offsetWidth) / 2;
    thumbs.scrollLeft = Math.max(0, left);
  };

  let frame = 0;
  const syncSoon = (box) => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      sync(box);
    });
  };

  // a gallery item's photo, or the Measurements figure's (which holds a bare img)
  const pic = (item) => item.querySelector('.product__media img') || item.querySelector('img');

  // a small Shopify CDN rendition for a thumb, off the gallery image's own url
  const small = (img) => {
    const url = new URL(img.getAttribute('src'), location.href);
    url.searchParams.set('width', '160');
    return url.href;
  };

  // an instant jump: a smooth scrollTo on a snap track is unreliable
  const go = (box, index) => {
    const { track } = parts(box);
    const slide = track.children[index];
    if (!slide) return;
    track.scrollLeft = slide.offsetLeft;
    sync(box);
  };

  const open = (box, items, index, toggle) => {
    const { track, thumbs } = parts(box);
    thumbs.replaceChildren(
      ...items.map((item, i) => {
        const img = document.createElement('img');
        img.src = small(pic(item));
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 160;
        img.height = 200;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.pdpLightboxThumb = i;
        button.setAttribute('aria-label', `${i + 1} / ${items.length}`);
        button.append(img);
        const li = document.createElement('li');
        li.append(button);
        return li;
      })
    );
    track.replaceChildren(
      ...items.map((item, i) => {
        const img = pic(item).cloneNode();
        img.removeAttribute('class');
        img.removeAttribute('style');
        img.sizes = '100vw';
        img.loading = i === index ? 'eager' : 'lazy';
        const li = document.createElement('li');
        li.className = 'pdp-lightbox__slide';
        li.append(img);
        return li;
      })
    );
    opener = toggle;
    scrollY = window.scrollY;
    document.body.classList.add('overflow-hidden');
    box.showModal();
    shown = -1;
    go(box, index);
    // the track takes focus so the arrow keys scroll it (focus on the close button would too, but
    // shows a ring on open)
    track.focus({ preventScroll: true });
    window.NaomiPDP?.track?.('gallery_viewer_opened', { index: index + 1, total: items.length });
  };

  // capture phase, so Dawn's modal-opener (a listener on the button itself) never hears it
  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target.closest?.('[data-pdp-gallery="stack"] .product__media-toggle');
      const box = document.querySelector('[data-pdp-lightbox]');
      if (!toggle || !box?.showModal) return;
      const items = photos();
      const item = toggle.closest('.product__media-item');
      let index = items.indexOf(item);
      if (index < 0) {
        // a video frame (the viewer holds photos only) is a tap target only with "+N" on it:
        // it opens on the first photo the cap keeps out
        index = item?.dataset.pdpMore ? items.findIndex((photo) => photo.classList.contains('is-capped')) : -1;
        if (index < 0) return;
        window.NaomiPDP?.track?.('gallery_more_opened', { hidden_count: Number(item.dataset.pdpMore) });
      } else if (item.dataset.pdpMore && items[index + 1]) {
        // the last photo shown with "+N" on it opens on the first photo the cap keeps out
        index += 1;
        window.NaomiPDP?.track?.('gallery_more_opened', { hidden_count: Number(item.dataset.pdpMore) });
      }
      event.stopPropagation();
      // detail 0 is a keyboard click: only then does focus go back to the photo on close, so a
      // tap never leaves a focus ring on it (owner 2026-09-27)
      open(box, items, index, event.detail === 0 ? toggle : null);
      if (event.detail !== 0) toggle.blur();
    },
    true
  );

  // the Measurements photo (pdp-spec.liquid) is out of the gallery: it opens alone, whole
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-pdp-spec-zoom]');
    const box = document.querySelector('[data-pdp-lightbox]');
    if (!toggle || !box?.showModal) return;
    open(box, [toggle.closest('.pdp-spec__photo')], 0, event.detail === 0 ? toggle : null);
    if (event.detail !== 0) toggle.blur();
  });

  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target.matches?.('[data-pdp-lightbox-track]')) syncSoon(event.target.closest('dialog'));
    },
    true
  );

  // on the photo itself, not the white around it: the img box fills the slide and the photo
  // sits centred in it at its contain fit
  const onPhoto = (event) => {
    const img = event.target.closest('.pdp-lightbox__slide img');
    if (!img?.naturalWidth) return false;
    const r = img.getBoundingClientRect();
    const s = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
    return (
      Math.abs(event.clientX - (r.left + r.width / 2)) <= (img.naturalWidth * s) / 2 &&
      Math.abs(event.clientY - (r.top + r.height / 2)) <= (img.naturalHeight * s) / 2
    );
  };

  document.addEventListener('click', (event) => {
    const box = event.target.closest?.('[data-pdp-lightbox]');
    if (!box) return;
    // a tap or click on the white around the photo closes it, as the close button does
    // (a keyboard click, detail 0, is always on a control)
    if (event.detail && !event.target.closest('button') && !onPhoto(event)) return box.close();
    if (event.target.closest('[data-pdp-lightbox-close]')) box.close();
    const step = event.target.closest('[data-pdp-lightbox-step]');
    if (step) go(box, current(parts(box).track) + Number(step.dataset.pdpLightboxStep));
    const thumb = event.target.closest('[data-pdp-lightbox-thumb]');
    if (thumb) go(box, Number(thumb.dataset.pdpLightboxThumb));
  });

  document.addEventListener('keydown', (event) => {
    const box = event.target.closest?.('[data-pdp-lightbox]');
    if (!box?.open || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    go(box, current(parts(box).track) + (event.key === 'ArrowRight' ? 1 : -1));
  });

  // Esc and the close button both end here
  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches?.('[data-pdp-lightbox]')) return;
      document.body.classList.remove('overflow-hidden');
      if (window.scrollY !== scrollY) window.scrollTo(0, scrollY);
      // the dialog hands focus back to whatever had it before; after a tap that must not be
      // the photo, or it keeps a ring
      if (opener) opener.focus({ preventScroll: true });
      else if (document.activeElement?.matches('.product__media-toggle')) document.activeElement.blur();
      opener = null;
      const { track, thumbs } = parts(event.target);
      track.replaceChildren();
      thumbs.replaceChildren();
    },
    true
  );
})();

/**
 * NAO-97: a sideways swipe on the first phone stack photo. People used to carousels swipe a photo
 * sideways; the stack scrolls down. The photo follows the finger a little with heavy
 * resistance and springs back, and every time the stack nudges up so the
 * next photo's top peeks in while the next dot in the rail marks itself (pdp-gallery.css),
 * which reads as "more below". Vertical gestures are left alone: passive listeners, the axis
 * is decided once per gesture, and nothing here ever calls preventDefault. Touches that start
 * on the drawer (its pull, the dot rail) are ignored.
 */
(() => {
  const phone = window.matchMedia('(max-width: 749px)');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  // a long soft settle, and an in-out for the return leg: the cue should glide, not bounce
  // (owner 2026-09-27, "a little more elegant")
  const settle = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const glide = 'cubic-bezier(0.65, 0, 0.35, 1)';
  let gesture = null;
  let nudge = null;

  const photos = () =>
    Array.from(document.querySelectorAll('.pdp-gallery__stack > .product__media-item')).filter(
      (item) => item.offsetParent !== null
    );

  // resistance: the offset grows ever slower and never passes 32px
  const band = (dx) => 32 * Math.tanh(dx / 90);

  document.addEventListener(
    'touchstart',
    (event) => {
      // a new touch cancels a running nudge, so it never fights a scroll
      nudge?.cancel();
      nudge = null;
      gesture = null;
      if (!phone.matches || event.touches.length !== 1) return;
      const item = event.target.closest?.('.pdp-gallery__stack > .product__media-item');
      if (!item || event.target.closest('.pdp-peek')) return;
      // the first photo only: further down the buyer already knows the stack scrolls
      // (owner 2026-09-28)
      if (item !== photos()[0]) return;
      const touch = event.touches[0];
      gesture = { item, x: touch.clientX, y: touch.clientY, axis: null, dx: 0, frame: 0 };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!gesture) return;
      const touch = event.touches[0];
      const dx = touch.clientX - gesture.x;
      const dy = touch.clientY - gesture.y;
      if (!gesture.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return;
        gesture.axis = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
        if (gesture.axis === 'y') {
          gesture = null;
          return;
        }
      }
      gesture.dx = dx;
      if (still.matches || gesture.frame) return;
      gesture.frame = requestAnimationFrame(() => {
        if (!gesture) return;
        gesture.frame = 0;
        gesture.item.style.transform = `translateX(${band(gesture.dx)}px)`;
      });
    },
    { passive: true }
  );

  const release = () => {
    if (!gesture || gesture.axis !== 'x') {
      gesture = null;
      return;
    }
    const { item, frame, dx } = gesture;
    gesture = null;
    cancelAnimationFrame(frame);
    if (!still.matches) {
      item.style.transform = '';
      item.animate([{ transform: `translateX(${band(dx)}px)` }, { transform: 'none' }], {
        duration: 520,
        easing: settle,
      });
    }
    // every sideways swipe on the first photo teaches again (owner 2026-09-27)
    const list = photos();
    const index = list.indexOf(item);
    // a single-photo stack has nothing below it
    if (index < 0 || index === list.length - 1) return;
    // the dot lights as the next photo peeks in and fades with the return (its transition,
    // pdp-gallery.css)
    const dot = document.querySelectorAll('.pdp-peek__dots li')[index + 1];
    const lit = setTimeout(() => dot?.classList.add('is-hint'), still.matches ? 0 : 330);
    const out = setTimeout(() => dot?.classList.remove('is-hint'), still.matches ? 900 : 1200);
    if (still.matches) return;
    // after the sideways settle has mostly landed: rise, rest a beat on the peek, glide back.
    // A fifth of the screen, so the next photo reads as a photo, not a sliver (owner 2026-09-30:
    // 36px was too subtle)
    const rise = `translateY(-${Math.round(Math.min(window.innerHeight * 0.2, 160))}px)`;
    nudge = item.parentElement.animate(
      [
        { transform: 'none', easing: settle },
        { transform: rise, offset: 0.38, easing: 'linear' },
        { transform: rise, offset: 0.55, easing: glide },
        { transform: 'none' },
      ],
      { duration: 1100, delay: 150 }
    );
    nudge.oncancel = () => {
      clearTimeout(lit);
      clearTimeout(out);
      dot?.classList.remove('is-hint');
    };
  };

  document.addEventListener('touchend', release, { passive: true });
  document.addEventListener('touchcancel', release, { passive: true });
})();
